import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { FaPlus, FaAward, FaTrash, FaPrint, FaSearch, FaEye, FaUserTie } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import PrintableExperienceCertificate from "./PrintableExperienceCertificate";
import EmployeeDetails from "../../Components/details/EmployeeDetails";

export default function ExperienceCertificateList() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printingCert, setPrintingCert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Experience_Certificate`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  const triggerPrint = (cert) => {
    setPrintingCert(cert);
    setTimeout(() => {
      handlePrint();
    }, 150);
  };

  const handleViewDetails = async (employeeId) => {
    if (!employeeId) {
      toast.error("Employee details not available");
      return;
    }
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${server}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployeeDetails(res.data.data);
    } catch (error) {
      toast.error("Failed to load employee details");
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/experienceCertificates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setCertificates(res.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to fetch Experience Certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCertificates();
  }, [token]);

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this Experience Certificate?",
      )
    )
      return;
    try {
      const res = await axios.delete(
        `${server}/experienceCertificates/deleteById?id=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.data.success) {
        toast.success("Experience Certificate deleted successfully");
        fetchCertificates();
      }
    } catch (error) {
      toast.error("Failed to delete Experience Certificate");
    }
  };

  const filteredCertificates = certificates.filter((c) => {
    const empName = c.employee?.name || c.employee?.Name || c.employeeName || "";
    const empCode = c.employee?.employeeCode || c.employee?.EmployeeCode || c.employeeCode || "";
    const certNum = c.certificateNumber || "";
    const term = searchTerm.toLowerCase();
    return (
      empName.toLowerCase().includes(term) ||
      empCode.toLowerCase().includes(term) ||
      certNum.toLowerCase().includes(term)
    );
  });

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaAward size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Staff Experience Certificates Register
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Manage all issued teaching and non-teaching employee experience
              certificates
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/my/employees/generate-exp-cert")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Issue Experience Cert
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Total Issued Certs
          </span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">
            {certificates.length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Academic Year
          </span>
          <div className="text-sm font-black text-emerald-600 dark:text-emerald-400 mt-1">
            2026 - 2027
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs col-span-2 sm:col-span-1">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">
            Search Filter
          </span>
          <div className="text-xs font-bold text-gray-700 dark:text-slate-300 mt-1 truncate">
            {searchTerm || "Showing All"}
          </div>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="text-xs font-black text-gray-700 dark:text-slate-300">
          Experience Certificates ({filteredCertificates.length})
        </div>

        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search Cert No, Staff Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
          />
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Cert Number</th>
                <th className="p-2.5">Employee Name</th>
                <th className="p-2.5">Staff Code</th>
                <th className="p-2.5">Issue Date</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-12 text-indigo-600 font-bold"
                  >
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading experience certificates...
                  </td>
                </tr>
              ) : filteredCertificates.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-12 text-gray-400 italic"
                  >
                    No experience certificates issued yet.
                  </td>
                </tr>
              ) : (
                filteredCertificates.map((cert) => (
                  <tr
                    key={cert.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition"
                  >
                    <td className="p-2.5 font-mono font-black text-indigo-600 dark:text-indigo-400">
                      {cert.certificateNumber}
                    </td>
                    <td className="p-2.5">
                      <button
                        onClick={() =>
                          handleViewDetails(
                            cert.employeeId || cert.employee?.id || cert.employee?.Id
                          )
                        }
                        className="font-black text-indigo-600 dark:text-indigo-400 hover:underline text-left flex items-center gap-1.5"
                        title="View Staff Profile Details"
                      >
                        <FaUserTie className="text-indigo-400 text-xs flex-shrink-0" />
                        {cert.employee?.name || cert.employee?.Name || cert.employeeName || "—"}
                      </button>
                    </td>
                    <td className="p-2.5 font-bold text-gray-700 dark:text-slate-300 font-mono">
                      {cert.employee?.employeeCode || cert.employee?.EmployeeCode || cert.employeeCode || "—"}
                    </td>
                    <td className="p-2.5 text-gray-500 font-medium whitespace-nowrap">
                      {new Date(
                        cert.issueDate || cert.createdAt,
                      ).toLocaleDateString()}
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() =>
                            handleViewDetails(
                              cert.employeeId || cert.employee?.id || cert.employee?.Id
                            )
                          }
                          className="p-1.5 text-emerald-600 bg-emerald-50 dark:bg-emerald-950 rounded-lg hover:bg-emerald-100 transition"
                          title="View Staff Details"
                        >
                          <FaEye size={11} />
                        </button>
                        <button
                          onClick={() => triggerPrint(cert)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Re-Print Certificate"
                        >
                          <FaPrint size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(cert.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Certificate"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="hidden">
        <PrintableExperienceCertificate
          ref={componentRef}
          certData={printingCert}
        />
      </div>

      {/* Staff Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaUserTie className="text-indigo-500" /> Staff Profile Details
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setEmployeeDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-xs font-bold px-2 py-1 rounded-lg"
              >
                ✕ Close
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 custom-scrollbar text-xs">
              {detailsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-indigo-600 font-bold">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mb-2"></div>
                  Loading staff details...
                </div>
              ) : employeeDetails ? (
                <EmployeeDetails
                  employee={employeeDetails}
                  onClose={() => {
                    setShowDetailsModal(false);
                    setEmployeeDetails(null);
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
